import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { HandlersActionResponse } from '../../app/athenaApiGen';
import { PROJECT_ACTIONS, ADMIN_SITEWIDE_ACCESS } from './aclActions';

/**
 * Hook to check user permissions for project-related actions.
 *
 * Permission checking follows the Deliverables pattern:
 * 1. Uses flattened actions array from Redux state.user.actions
 * 2. Admin users with 'admin-sitewide-access' have all permissions
 * 3. Backend automatically filters results based on JWT claims
 *
 * @param projectId - Optional project ID (for future per-row checks)
 * @param programId - Optional program ID (for future per-row checks)
 * @returns Object with boolean permission flags and utility functions
 */
export const useProjectPermissions = (projectId?: string, programId?: string) => {
  const { actions } = useSelector((state: RootState) => state.user);

  const permissions = useMemo(() => {
    /**
     * Check if user has admin sitewide access (bypasses all ACL)
     */
    const hasAdminAccess = (): boolean => {
      if (!actions || actions.length === 0) return false;
      return actions.some(
        (action: HandlersActionResponse) => action?.action === ADMIN_SITEWIDE_ACCESS
      );
    };

    /**
     * Check if user has a specific action available
     * This checks the flattened actions array from all roles
     * Following Deliverables pattern - backend handles scope filtering
     */
    const hasAction = (actionName: string): boolean => {
      if (!actions || actions.length === 0) return false;
      return actions.some(
        (action: HandlersActionResponse) => action?.action === actionName
      );
    };

    /**
     * Check if user has action for a specific project
     * For now, this just checks if the user has the action at all.
     * The backend handles scope-based filtering via JWT claims.
     */
    const hasProjectAction = (actionName: string, _targetProjectId?: string, _targetProgramId?: string): boolean => {
      // Check admin access first
      if (hasAdminAccess()) return true;
      // Check if user has the action - backend handles scope filtering
      return hasAction(actionName);
    };

    /**
     * Check if user has action for a specific program
     * For now, this just checks if the user has the action at all.
     * The backend handles scope-based filtering via JWT claims.
     */
    const hasProgramAction = (actionName: string, _targetProgramId?: string): boolean => {
      // Check admin access first
      if (hasAdminAccess()) return true;
      // Check if user has the action - backend handles scope filtering
      return hasAction(actionName);
    };

    // Pre-computed permission flags
    const isAdmin = hasAdminAccess();

    return {
      // Admin bypass
      isAdmin,

      // Project list permissions
      canListProjects: isAdmin || hasAction(PROJECT_ACTIONS.LIST_PROJECTS),

      // Create permission
      canCreateProject: isAdmin || hasAction(PROJECT_ACTIONS.CREATE_PROJECT),

      // View permission
      canViewProject: isAdmin || hasAction(PROJECT_ACTIONS.GET_PROJECT),

      // Edit permission
      canEditProject: isAdmin || hasAction(PROJECT_ACTIONS.UPDATE_PROJECT),

      // Archive permission
      canArchiveProject: isAdmin || hasAction(PROJECT_ACTIONS.ARCHIVE_PROJECT),

      // Download permission
      canDownloadProject: isAdmin || hasAction(PROJECT_ACTIONS.DOWNLOAD_PROJECT),

      // Copy link permission
      canCopyProjectLink: isAdmin || hasAction(PROJECT_ACTIONS.COPY_PROJECT_LINK),

      // Print permission
      canPrintProject: isAdmin || hasAction(PROJECT_ACTIONS.PRINT_PROJECT),

      // Utility functions for dynamic checks
      hasAction,
      hasProjectAction,
      hasProgramAction,
      hasAdminAccess,
    };
  }, [actions, projectId, programId]);

  return permissions;
};

/**
 * Hook to check permissions for a specific project row in a table
 * Use this when you need to check permissions for each row independently
 *
 * @param project - Project object with projectId and programId
 * @returns Permission flags for that specific project
 */
export const useProjectRowPermissions = (project?: { projectId?: string; programId?: string }) => {
  return useProjectPermissions(project?.projectId, project?.programId);
};

export default useProjectPermissions;
